import React, { useEffect, useState } from "react";
import api, { fmt, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";

const TYPES = [
  "principal_disbursement", "principal_repayment",
  "interest_accrual", "interest_payment",
  "fx_deal", "mtm_revaluation",
  "bank_credit", "bank_debit",
];

export default function TransactionsPage() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/transactions").then(({ data }) => setRows(data));
  }, []);

  const filtered = rows.filter((r) =>
    !filter || r.txn_type === filter
  );

  const exportCsv = () => {
    const header = ["Date", "Type", "Reference", "Description", "Amount", "Currency", "Counter Amt", "Counter Ccy", "FX Rate", "Reconciled"];
    const lines = [header.join(",")];
    filtered.forEach((r) => {
      lines.push([
        r.value_date, r.txn_type, r.reference || "", `"${(r.description || "").replace(/"/g, '""')}"`,
        r.amount, r.currency, r.counter_amount ?? "", r.counter_currency ?? "", r.fx_rate ?? "", r.reconciled ? "Y" : "N",
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "transactions.csv"; a.click();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Ledger"
        title="Transactions"
        subtitle="Single auditable record of every principal, interest, FX and revaluation event."
        testid="txn-title"
        actions={
          <>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="txn-filter" className="border border-gray-300 px-3 py-2 text-xs font-mono">
              <option value="">All Types</option>
              {TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
            <button onClick={exportCsv} data-testid="export-csv" className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-50">Export CSV</button>
          </>
        }
      />

      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="txn-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["Date", "Type", "Reference", "Description", "Amount", "Ccy", "Counter", "FX", "Reconciled"].map((h) => (
                  <th key={h} className={`px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300 ${["Amount","Counter","FX"].includes(h) ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.value_date)}</td>
                  <td className="px-4 py-3 text-[10px] tracking-[0.15em] uppercase font-bold">{r.txn_type.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.reference || "—"}</td>
                  <td className="px-4 py-3 text-gray-700">{r.description}</td>
                  <td className={`px-4 py-3 text-right font-mono ${(r.direction_sign ?? 0) < 0 ? "text-[#DC2626]" : ""}`}>{fmt(r.amount)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.currency}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{r.counter_amount ? `${fmt(r.counter_amount)} ${r.counter_currency}` : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{r.fx_rate ? r.fx_rate.toFixed(4) : "—"}</td>
                  <td className="px-4 py-3 text-[10px] font-bold">
                    {r.status === "pending" ? <span className="text-[#EAB308]">⏳ Pending</span> : r.status === "rejected" ? <span className="text-[#DC2626]">✕ Rejected</span> : r.reconciled ? <span className="text-gray-900">✓ Yes</span> : <span className="text-gray-400">—</span>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-500">No transactions to display.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
